import { ReactNode, useCallback } from "react";

import debounce from "lodash.debounce";

import { t } from "@lingui/macro";
import { useResumeStore } from "@/client/stores/resume";
import { useSaveStatusStore } from "@/client/stores/useSaveStatusStore";

import { basicsSchema } from "@reactive-resume/schema";
import { Input, Label, Switch, Tooltip } from "@reactive-resume/ui";

import AiAssistantIcon from "../../../Icons/AiAssistantIcon";

import { CollapsibleSection } from "../../CollapsibleSection";

import { CustomFieldsSection } from "./custom/section";
import { PictureSection } from "./picture/section";
import { SectionIcon } from "./shared/section-icon";
import { URLInput } from "./shared/url-input";

export const BasicsSection = () => {
  const setValue = useResumeStore((state) => state.setValue);
  const basics = useResumeStore((state) => state.resume.data.basics);

  const setStatus = useSaveStatusStore((s) => s.setStatus);

  const hasNameError = !basicsSchema.pick({ name: true }).safeParse({ name: basics.name }).success;
  const hasEmailError = !basicsSchema.pick({ email: true }).safeParse({ email: basics.email })
    .success;

  // handles the status indicator correct status update
  const handleStatusUpdate = useCallback(
    debounce((value: string) => {
      const hasError = !basicsSchema.pick({ email: true }).safeParse({ email: value }).success;
      hasError ? setStatus("error") : setStatus("saved");
    }, 500),
    [setStatus],
  );

  const headerHTML: ReactNode = (
    <div className="flex items-center gap-x-4">
      <SectionIcon id="basics" size={18} />
      <h2 className="line-clamp-1 text-base font-bold text-gray-800 lg:text-xl">Basics</h2>
    </div>
  );

  const contentHTML: ReactNode = (
    <main className="grid gap-4 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <PictureSection />
      </div>

      <div className="space-y-4 sm:col-span-2">
        <Label htmlFor="basics.name">{t`Full Name`}</Label>
        <Input
          id="basics.name"
          value={basics.name}
          hasError={hasNameError}
          onChange={(event) => {
            setValue("basics.name", event.target.value);
          }}
        />
      </div>

      <div className="space-y-1.5 sm:col-span-2">
        <Label htmlFor="basics.headline">{t`Headline`}</Label>
        <Input
          id="basics.headline"
          value={basics.headline}
          onChange={(event) => {
            setValue("basics.headline", event.target.value);
          }}
        />
        <div className="flex w-full items-center justify-end gap-x-2 pt-2">
          <span className="text-muted-foreground text-xs">
            {basics.canAICustomizeHeadline ? (
              <Tooltip content="Customization for each job application using AI is Enabled">
                <span className="flex items-center gap-x-1">
                  <AiAssistantIcon size={18} />
                  Tailor for Each Job
                </span>
              </Tooltip>
            ) : (
              <Tooltip content="Customization for each job application using AI is Disabled, turn on to enable">
                <span className="flex items-center gap-x-1 opacity-50">
                  <AiAssistantIcon size={18} className="grayscale" />
                  Tailor for Each Job
                </span>
              </Tooltip>
            )}
          </span>
          <Switch
            aria-label={
              basics.canAICustomizeHeadline
                ? "Toggle customization to disabled"
                : "Toggle customization to enabled"
            }
            className="data-[state=checked]:bg-[#047857]"
            checked={basics.canAICustomizeHeadline}
            onCheckedChange={(checked) => {
              setValue("basics.canAICustomizeHeadline", checked);
            }}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="basics.email">{t`Email`}</Label>
        <Input
          id="basics.email"
          placeholder="john.doe@example.com"
          value={basics.email}
          hasError={hasEmailError}
          onChange={(event) => {
            setStatus("saving");
            handleStatusUpdate(event.target.value); // updating correct status
            setValue("basics.email", event.target.value);
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="basics.url">{t`Website`}</Label>
        <URLInput
          id="basics.url"
          value={basics.url}
          placeholder="https://example.com"
          onChange={(value) => {
            setValue("basics.url", value);
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="basics.phone">{t`Phone`}</Label>
        <Input
          id="basics.phone"
          placeholder="+1 (123) 4567 7890"
          value={basics.phone}
          onChange={(event) => {
            setValue("basics.phone", event.target.value);
          }}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="basics.location">{t`Location`}</Label>
        <Input
          id="basics.location"
          value={basics.location}
          onChange={(event) => {
            setValue("basics.location", event.target.value);
          }}
        />
      </div>

      <CustomFieldsSection className="sm:col-span-2" />
    </main>
  );

  return (
    <>
      {/* High Impact Keywords max limit slider */}
      {/* Un-comment below code to show "High Impact Keywords max limit slider" */}
      {/* <div className="space-y-1.5">
        <Separator className="mb-2" />
        <div>
          <Label htmlFor="basics.noOfKeywordsToUseFromJobDescription">
            Max No. of High Impact Keywords
          </Label>
          <p className="text-muted-foreground text-sm">
            To use from job description to customize this resume for each job application.
          </p>
          <div className="flex items-center gap-x-4 py-1">
            <Slider
              min={5}
              max={30}
              step={1}
              value={[basics.noOfKeywordsToUseFromJobDescription]}
              onValueChange={(value) => {
                setValue("basics.noOfKeywordsToUseFromJobDescription", value[0]);
              }}
            />
            <span className="text-base font-bold">
              {basics.noOfKeywordsToUseFromJobDescription}
            </span>
          </div>
          {basics.noOfKeywordsToUseFromJobDescription > 15 && (
            <p className="mt-1 text-sm text-yellow-500">
              Warning: Using a higher number of keywords may lead to irrelevant keywords being
              included in the optimization process
            </p>
          )}
        </div>
        <Separator />
      </div> */}

      <CollapsibleSection id={"basics"} header={headerHTML} content={contentHTML} />
    </>
  );
};
